import {
  GetItemPropsOptions,
  UseComboboxReturnValue
} from "downshift";
import debounce from "lodash.debounce";
import * as React from "react";
import styled, { css } from "styled-components";

import { InputCss } from "src/library/components/Forms/Input";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Text } from "src/library/components/Text";
import Icon from "src/library/components/Icon/Icon";
import type { IApiError } from "src/library/utils/errors/apiError";
import { Icons } from "src/library/components/Icon/types";
import { PopOutWrapper } from "src/library/components/ActionsPopOut";
import DropdownItem from "src/library/components/Dropdown/DropdownItem";
import { IDrop } from "src/core/drops/api/drops.api";
import { ITerminology } from "src/library/utils";

interface Props {
  // data = the string you'd like to search for
  onSearch: (data: string) => void;
  onClose: () => void;
  loading: boolean;
  searchResults: IDrop[];
  placeholder: string;
  error: IApiError | null;
  downshiftProps: UseComboboxReturnValue<IDrop>;
  autoFocus?: boolean;
  terminology?: ITerminology;
}

export interface SearchRowProps extends GetItemPropsOptions<IDrop> {
  drop: IDrop;
  disabled: boolean;
  highlighted: boolean;
}

interface ElementPosition {
  x: number;
  y: number;
}

const unsetPosition = {
  x: 0,
  y: 0
};

/**
 * When active, the input serves as a search bar enabling user to search and select a drop.
 * Results of the search appear below the input.
 */
export const DropSearchComponent: React.FunctionComponent<Props> = ({
  downshiftProps, onSearch, loading, searchResults, placeholder, error, onClose, autoFocus, terminology
}) => {
  const [ searchTerm, setSearchTerm ] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  const performSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    // hardcoded minimum no need for flexibilty yet
    const minimumChars = 1;

    if (e.target.value.length >= minimumChars) {
      onSearch(e.target.value);
      setSearchTerm(e.target.value);
    } else if (e.target.value.length === 0) {
      onSearch("");
      setSearchTerm("");
    }
  }, 500);

  React.useEffect(() => {
    if (autoFocus) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [ autoFocus ]);

  return (
    <SearchComponent className="drop-search-component">
      <div className="search-wrapper">
        <Icon
          className="search-icon"
          icon={Icons.SEARCH}
          $fillColor="lightGrey"
        />

        <input
          {...downshiftProps.getInputProps({
            placeholder: placeholder || "Search",
            className: "search-input",
            id: "search",
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              e.persist();
              performSearch(e);
            },
            onFocus: (e: React.ChangeEvent<HTMLInputElement>) => {
              e.persist();
              downshiftProps.openMenu();
            }
          }, { suppressRefError: true })}
          ref={inputRef}
        />

        {!loading && (
          <Icon
            className="close-search"
            icon={Icons.CLOSE}
            $fillColor="primaryDark"
            onClick={onClose}
          />
        )}
      </div>

      <div>
        <StyledPopWrapper className="pull-left push-top">
          {searchTerm.length > 0 || searchResults.length > 0 ? (
            <ul
              className={`${downshiftProps.isOpen && "active"}`}
              {...downshiftProps.getMenuProps({}, { suppressRefError: true })}
            >
              <div className="header-wrapper">
                <Text weight="bold">
                  {searchTerm.length ? `Showing results for "${searchTerm}"` : ""}
                </Text>

                {loading && (
                  <LoadingSpinner
                    type="flat"
                    position="inline"
                    size={10}
                  />
                )}
              </div>

              {downshiftProps.isOpen && (
                <>
                  {searchResults.map((item, index) => (
                    <div
                      key={`${item.id}-${index}`}
                      {...downshiftProps.getItemProps({
                        item,
                        index
                      })}
                    >
                      <DropdownItem highlighted={index === downshiftProps.highlightedIndex}>
                        <Text className="emphasize">
                          {`(${item.branchNumber})`}
                        </Text>

                        <Text className="details">
                          {item.name}
                        </Text>
                      </DropdownItem>
                    </div>
                  ))}

                  {!searchResults.length && !error && !loading && (
                    <DropdownItem>
                      <Text className="details">
                        Nothing found
                      </Text>
                    </DropdownItem>
                  )}

                  {error && !loading && (
                    <DropdownItem>
                      <Text className="error">
                        An unknown error occurred whilst performing your search, please try again.
                      </Text>
                    </DropdownItem>
                  )}
                </>
              )}
            </ul>
          ) : (
            <div className="pre-search">
              <Text className="details">
                {`Start typing to find ${terminology?.drop.singular.text || "drops"}`}
              </Text>
            </div>
          )}
        </StyledPopWrapper>
      </div>

      {loading && (
        <LoadingSpinner
          className="loading-spinner"
          type="circular"
          position="inline"
          size={20}
        />
      )}
    </SearchComponent>
  );
};

const StyledPopWrapper = styled(PopOutWrapper)`
  ${({ theme }) => css`
    margin: 0;
    width: 400px;
    position: absolute;
    top: 100%;
    left: 0;

    .header-wrapper {
      display: flex;
      justify-content: space-between;
      padding: 0 ${theme.spacing.padding.md};
    }

    .pre-search {
      padding: 0 ${theme.spacing.padding.md};
      padding-bottom: ${theme.spacing.padding.md};
    }
  `}
`;

const SearchComponent = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex: 1;
    height: 40px;
    max-width: 400px;
    position: relative;

    [role="combobox"] {
      width: 100%;
    }

    .search-wrapper {
      width: 100%;
      position: relative;

      .search-icon {
        position: absolute;
        left: 5px;
        top: 50%;
        transform: translateY(-50%);
      }

      .close-search {
        position: absolute;
        right: 5px;
        top: 50%;
        transform: translateY(-50%);
        
        &:hover {
          cursor: pointer;
        }
      }

      .search-input {
        ${InputCss};
        width: 240px;
        max-width: 100%;
        border: solid 2px ${theme.colors.lightGrey};
        border-radius: ${theme.borderRadius};
        padding-left: 35px;
        transition: ${theme.transition.quickest};
        font-size: ${theme.fonts.sizes.p};

        &:hover,
        &:focus {
          border-color: ${theme.colors.primaryDark};
          background-color: ${theme.colors.white};
        }

        width: 100%;
        height: 100%;
        font-size: ${theme.fonts.sizes.p};

        &:focus {
          ::placeholder {
            color: ${theme.colors[ "primaryDark" ]};
          }
        }
      }

      &:hover {
        svg {
          fill: ${theme.colors.lightGrey};
        }
      }
    }

    .loading-spinner {
      position: absolute;
      top: 50%;
      right: 15px;
      transform: translateY(-50%);
    }
  `};
`;
